import { UUID, uuidv7 } from "uuidv7"

import { parseUser } from "~/utils/flatbuffers/user"

import type { UserBuf } from "../generated/user"

import { apiIdFor, hasGroup, orderedId, Property, PropertyGroup, PropertySchema, PropertyStatus } from "../entity"
import { InterfaceSelection } from "../entity/InterfaceSelection"

export class InterfaceUser {
  _user: User

  constructor(user: User) {
    this._user = user
  }

  static from(user?: User) {
    if (user)
      return new InterfaceUser(user)
  }

  static fromBuf(buf: UserBuf): InterfaceUser {
    return new InterfaceUser(parseUser(buf))
  }

  /**
   * NOTE: UserEvent is just an alias for User (for now) but this may change
   */
  static fromEvent(event: UserEvent): InterfaceUser {
    return new InterfaceUser(event)
  }

  get properties() {
    return this._user.properties
  }

  get selection() {
    return new InterfaceSelection(this._user.selection)
  }

  inner(): Readonly<User> {
    return this._user
  }

  property(id: Property): UserProperty | undefined {
    return this._user.properties[id]
  }

  static clientPreHash() {
    return `00010000-${uuidv7()}`
  }

  static generateClientOnlyUserId() {
    return `00010003-${uuidv7()}`
  }

  type() {
    return apiIdFor(this.userId)
  }

  get userId(): string {
    return this._user.userId
  }

  email() {
    const property = this.property(Property.USER_EMAIL)
    if (!property)
      return
    if (property.value.type === PropertySchema.EmailValue)
      return property.value.email
    console.error("Invalid schema", property.value)
    throw new Error("`Schema invalid for email")
  }

  handle(): EntityHandle | undefined {
    const property = this.property(Property.USER_HANDLE)
    if (!property)
      return
    if (property.value.type === PropertySchema.EntityHandleValue)
      return property.value.entityHandle
    console.error("Invalid schema", property.value)
    throw new Error("`Schema invalid for handle")
  }

  addProperty(property: UserProperty) {
    this._user.properties[property.propertyId] = property
  }

  removeProperty(property: UserProperty) {
    delete this._user.properties[property.propertyId]
  }

  /**
   * Merges two interface users based on their properties. The "newer" one doesn't have
   * to be newer as such, but in the case of a "tie" we take its properties.
   */
  mergeWithNewer(newer?: InterfaceUser) {
    if (!newer)
      return this
    if (newer.userId !== this.userId)
      throw new Error("Cannot merge users with different user ids!")
    for (const prop of Object.values(newer._user.properties)) {
      const thisProp = this._user.properties[prop!.propertyId]
      if (!prop)
        continue
      else if (!thisProp)
        this._user.properties[prop!.propertyId] = prop
      else if (thisProp && (thisProp.epochId ?? 0) < (prop.epochId ?? 0))
        this._user.properties[prop!.propertyId] = prop
      else if (thisProp && (thisProp.version ?? 0) < (prop.version ?? 0))
        this._user.properties[prop!.propertyId] = prop
      else if (thisProp && (thisProp.timestamp) < (prop.timestamp!))
        this._user.properties[prop!.propertyId] = prop
      else if (thisProp && thisProp.status !== PropertyStatus.StatusCurrent && prop.status === PropertyStatus.StatusCurrent)
        this._user.properties[prop!.propertyId] = prop
    }
    // Return a new Interface user with old inner because otherwise the reactivity
    // hooks in vue don't update (the hook is attached to the outermost class's
    // reflection methods)
    return new InterfaceUser(this._user)
  }

  static blank(userId: string) {
    const selection = InterfaceSelection.nothing().inner()
    return new InterfaceUser({ userId, properties: {}, selection })
  }

  /**
   * Optional as user might not have this property. You almost certainly
   * want to use the computed value in useSelf instead
   */
  interfaceSettings() {
    const property = this.property(Property.USER_INTERFACE_SETTINGS)
    if (!property)
      return
    if (property.value.type === PropertySchema.UserInterfaceSettingsValue)
      return property.value.userInterfaceSettings
    console.error("Invalid schema", property.value)
    throw new Error("`Schema invalid for interface settings")
  }

  static async sidebarIndexPropertyId(fractionalIndex: string) {
    const encoder = new TextEncoder()
    const data = encoder.encode(fractionalIndex)
    const hash = await window.crypto.subtle.digest("SHA-256", data)
    const uuid = UUID.ofInner(new Uint8Array(hash.slice(0, 16)))
    const groupStr = PropertyGroup.UserSidebarOrderedItemMetaProperty.toString(16).padStart(8, "0")
    return `${groupStr}-${uuid.toString()}` as Property
  }

  static async createPropertyForSidebarFile({ userId, fileId, fractionalIndex, timestamp }: { userId: string, fileId: string, fractionalIndex: string, timestamp: string }): Promise<UserProperty> {
    return {
      userId,
      propertyId: await InterfaceUser.sidebarIndexPropertyId(fractionalIndex),
      schema: PropertySchema.UserSidebarItemIdValue,
      version: 0,
      value: { type: PropertySchema.UserSidebarItemIdValue, UserSidebarItemId: { fractionalIndex, fileItemId: fileId } },
      status: PropertyStatus.StatusPending,
      timestamp,
    }
  }

  static createPropertyForSidebarSearch({ searchId, userId, fractionalIndex }: { userId: string, searchId: string, fractionalIndex: string }): UserProperty {
    throw new Error("unimplemented")
    return {
      userId,
      propertyId: orderedId(PropertyGroup.UserSidebarOrderedItemMetaProperty, 0),
      schema: PropertySchema.UserSidebarItemIdValue,
      version: 0,
      value: { type: PropertySchema.UserSidebarItemIdValue, UserSidebarItemId: { fractionalIndex, searchItemId: searchId } },
      status: PropertyStatus.StatusPending,
      timestamp: uuidv7(),
    }
  }

  /** Returns all of the user object's sidebar entries */
  sidebarEntryProperties() {
    const properties: UserProperty[] = []
    for (const property of Object.values(this._user.properties)) {
      if (!property)
        continue
      if (!hasGroup(property.propertyId as Property, PropertyGroup.UserSidebarOrderedItemMetaProperty))
        continue
      properties.push(property)
    }
    return properties
  }
}
